import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  CheckCircle,
  Calendar,
  Clock,
  Users,
  Edit,
  AlertTriangle,
  FileText,
  DollarSign,
  Briefcase,
  FolderOpen,
  Play,
  Square,
  Bell,
  Trash2
} from 'lucide-react';
import { taskService, caseService, projectService, userService, calendarService, documentService } from '../../services/api';
import useModal from '../../hooks/useModal';
import useNotification from '../../hooks/useNotification';
import Notification from '../../components/common/Notification';
import Breadcrumb from '../../components/common/Breadcrumb';
import { useAuth } from '../../contexts/AuthContext';

// --- Standalone Modal Components (defined outside TaskDetail to prevent focus loss on re-render) ---

const AssignModal = ({ onClose, task, assigningUser, onUnassign, onAssign, availableUsers }) => {
  const { handleBackdropMouseDown, handleBackdropMouseUp } = useModal(true, onClose);

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onMouseDown={handleBackdropMouseDown}
      onMouseUp={handleBackdropMouseUp}
    >
      <div
        className="bg-white rounded-lg p-6 w-full max-w-md mx-4"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Assign Task</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            &times;
          </button>
        </div>

        <div className="space-y-3 max-h-64 overflow-y-auto">
          {task.assigned_to && (
            <button
              onClick={onUnassign}
              disabled={assigningUser}
              className="w-full text-left p-3 border border-gray-200 rounded-lg hover:bg-gray-50 flex items-center disabled:opacity-50"
            >
              <div className="bg-gray-100 p-2 rounded-full mr-3">
                <Users className="h-4 w-4 text-gray-600" />
              </div>
              <div>
                <div className="font-medium text-gray-700">Unassign Task</div>
                <div className="text-sm text-gray-500">Remove current assignment</div>
              </div>
            </button>
          )}

          {availableUsers.map((user) => (
            <button
              key={user.id}
              onClick={() => onAssign(user.id)}
              disabled={assigningUser || user.id === task.assigned_to}
              className={`w-full text-left p-3 border rounded-lg flex items-center disabled:opacity-50 ${
                user.id === task.assigned_to
                  ? 'border-yellow-500 bg-gray-50'
                  : 'border-gray-200 hover:bg-gray-50'
              }`}
            >
              <div className={`p-2 rounded-full mr-3 ${
                user.role === 'attorney' ? 'bg-gray-100' :
                user.role === 'paralegal' ? 'bg-green-100' :
                user.role === 'admin' ? 'bg-purple-100' :
                'bg-gray-100'
              }`}>
                <Users className={`h-4 w-4 ${
                  user.role === 'attorney' ? 'text-yellow-600' :
                  user.role === 'paralegal' ? 'text-green-600' :
                  user.role === 'admin' ? 'text-purple-600' :
                  'text-gray-600'
                }`} />
              </div>
              <div className="flex-1">
                <div className="font-medium">{user.first_name} {user.last_name}</div>
                <div className="text-sm text-gray-500">{user.email}</div>
                <div className="text-xs text-gray-400 capitalize">{user.role}</div>
              </div>
              {user.id === task.assigned_to && (
                <div className="ml-2 text-yellow-600 text-sm font-medium">Currently Assigned</div>
              )}
            </button>
          ))}
        </div>

        {availableUsers.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <Users className="mx-auto h-8 w-8 mb-2" />
            <p>No team members available</p>
          </div>
        )}

        <div className="mt-6 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:text-gray-800">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

const TimeLogModal = ({ onClose, task, loggingTime, setLoggingTime, setTask, timerSeconds, setTimerSeconds, showWarning, showError }) => {
  const [localHours, setLocalHours] = useState('');
  const [localDescription, setLocalDescription] = useState('');
  const tenthsPresets = [0.1, 0.2, 0.3, 0.5, 1.0, 2.0];

  const { handleBackdropMouseDown, handleBackdropMouseUp } = useModal(true, onClose);

  const formatTimeDisplay = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
    } else {
      return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    }
  };

  const handleSubmit = async () => {
    try {
      setLoggingTime(true);
      const additionalHours = parseFloat(localHours) || 0;
      if (additionalHours <= 0) {
        showWarning('Please enter a valid time amount');
        return;
      }
      await taskService.logTime(task.id, {
        hours: additionalHours,
        description: localDescription || null
      });
      const newActualHours = (task.actual_hours || 0) + additionalHours;
      setTask(prev => ({ ...prev, actual_hours: newActualHours }));
      const newTimerSeconds = Math.round(newActualHours * 3600);
      setTimerSeconds(newTimerSeconds);
      onClose();
    } catch (error) {
      console.error('Error logging time:', error);
      showError('Failed to log time. Please try again.');
    } finally {
      setLoggingTime(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onMouseDown={handleBackdropMouseDown}
      onMouseUp={handleBackdropMouseUp}
    >
      <div
        className="bg-white rounded-lg p-6 w-full max-w-md mx-4"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Log Billable Time</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            &times;
          </button>
        </div>

        <div className="space-y-4">
          <div className="bg-gray-50 p-3 rounded-lg">
            <div className="text-sm text-gray-600 mb-1">Current Total Time</div>
            <div className="font-mono text-lg font-semibold">{formatTimeDisplay(timerSeconds)}</div>
            <div className="text-sm text-gray-500">{(task.actual_hours || 0).toFixed(1)} hours</div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Hours to Add
            </label>
            <div className="flex items-center space-x-3">
              <input
                type="number"
                min="0.1"
                step="0.1"
                placeholder="0.0"
                value={localHours}
                onChange={(e) => setLocalHours(e.target.value)}
                className="w-24 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 text-lg font-mono"
                autoFocus
              />
              <div className="flex flex-wrap gap-1">
                {tenthsPresets.map(val => (
                  <button
                    key={val}
                    type="button"
                    onClick={() => setLocalHours(String(val))}
                    className={`px-2.5 py-1.5 rounded text-sm border ${
                      localHours === String(val)
                        ? 'bg-yellow-100 border-yellow-400 text-yellow-800'
                        : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    {val}h
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description (Optional)
            </label>
            <textarea
              rows={3}
              placeholder="Brief description of work performed..."
              value={localDescription}
              onChange={(e) => setLocalDescription(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
            />
          </div>

          {localHours && parseFloat(localHours) > 0 && (
            <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
              <div className="text-sm text-gray-800 font-medium">
                Adding: {parseFloat(localHours).toFixed(1)} hours
              </div>
              <div className="text-sm text-yellow-600">
                New Total: {((task.actual_hours || 0) + parseFloat(localHours)).toFixed(1)} hours
              </div>
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
            disabled={loggingTime}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loggingTime || !localHours || parseFloat(localHours) <= 0}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {loggingTime ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Logging...
              </>
            ) : (
              <>
                <DollarSign className="mr-2 h-4 w-4" />
                Log Time
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const StatusHistoryModal = ({ onClose, statusHistory }) => {
  const { handleBackdropMouseDown, handleBackdropMouseUp } = useModal(true, onClose);

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onMouseDown={handleBackdropMouseDown}
      onMouseUp={handleBackdropMouseUp}
    >
      <div
        className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[80vh] overflow-hidden flex flex-col"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Status History</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            &times;
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="space-y-3">
            {statusHistory.map((change) => (
              <div key={change.id} className="border-l-4 border-gray-200 pl-3 py-2 bg-gray-50 rounded-r-lg">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">
                      {change.from_status ? (
                        <>
                          <span className="text-gray-500">{change.from_status}</span>
                          <span className="mx-2">&rarr;</span>
                          <span className="text-yellow-600">{change.to_status}</span>
                        </>
                      ) : (
                        <span className="text-yellow-600">{change.to_status}</span>
                      )}
                    </p>
                    <p className="text-xs text-gray-600 mt-1">{change.comment}</p>
                  </div>
                  <div className="text-xs text-gray-500 text-right ml-4">
                    <p className="font-medium">{change.changed_by}</p>
                    <p>{new Date(change.changed_at).toLocaleString()}</p>
                  </div>
                </div>
              </div>
            ))}

            {statusHistory.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <FileText className="mx-auto h-8 w-8 mb-2" />
                <p>No status changes yet</p>
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:text-gray-800">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const AddDocumentModal = ({ onClose, documentData, setDocumentData, uploadingDocument, onUpload, onFileChange }) => {
  const { handleBackdropMouseDown, handleBackdropMouseUp } = useModal(true, onClose);

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onMouseDown={handleBackdropMouseDown}
      onMouseUp={handleBackdropMouseUp}
    >
      <div
        className="bg-white rounded-lg p-6 w-full max-w-md mx-4"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Add Document to Task</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            &times;
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Document Name *
            </label>
            <input
              type="text"
              value={documentData.name}
              onChange={(e) => setDocumentData(prev => ({ ...prev, name: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
              placeholder="Enter document name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select File *
            </label>
            <input
              type="file"
              onChange={onFileChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
              accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg,.gif"
            />
            {documentData.file && (
              <div className="mt-2 text-sm text-gray-600">
                Selected: {documentData.file.name} ({Math.round(documentData.file.size / 1024)} KB)
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              rows={3}
              value={documentData.description}
              onChange={(e) => setDocumentData(prev => ({ ...prev, description: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
              placeholder="Optional description for the document"
            />
          </div>

          <div className="text-sm text-gray-500">
            <strong>Note:</strong> This is a demo implementation. In a full system, documents would be securely uploaded to cloud storage.
          </div>
        </div>

        <div className="mt-6 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
            disabled={uploadingDocument}
          >
            Cancel
          </button>
          <button
            onClick={onUpload}
            disabled={uploadingDocument || !documentData.name || !documentData.file}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {uploadingDocument ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Uploading...
              </>
            ) : (
              <>
                <FileText className="mr-2 h-4 w-4" />
                Upload Document
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const ScheduleTimeModal = ({ onClose, scheduleData, setScheduleData, schedulingEvent, onSchedule }) => {
  const { handleBackdropMouseDown, handleBackdropMouseUp } = useModal(true, onClose);

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onMouseDown={handleBackdropMouseDown}
      onMouseUp={handleBackdropMouseUp}
    >
      <div
        className="bg-white rounded-lg p-6 w-full max-w-md mx-4"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Schedule Time for Task</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            &times;
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Event Title *
            </label>
            <input
              type="text"
              value={scheduleData.title}
              onChange={(e) => setScheduleData(prev => ({ ...prev, title: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
              placeholder="Enter event title"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date *
            </label>
            <input
              type="date"
              value={scheduleData.date}
              onChange={(e) => setScheduleData(prev => ({ ...prev, date: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Time *
              </label>
              <input
                type="time"
                value={scheduleData.start_time}
                onChange={(e) => setScheduleData(prev => ({ ...prev, start_time: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                End Time *
              </label>
              <input
                type="time"
                value={scheduleData.end_time}
                onChange={(e) => setScheduleData(prev => ({ ...prev, end_time: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              rows={3}
              value={scheduleData.description}
              onChange={(e) => setScheduleData(prev => ({ ...prev, description: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
              placeholder="Optional description for the calendar event"
            />
          </div>
        </div>

        <div className="mt-6 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
            disabled={schedulingEvent}
          >
            Cancel
          </button>
          <button
            onClick={onSchedule}
            disabled={schedulingEvent || !scheduleData.title || !scheduleData.date || !scheduleData.start_time || !scheduleData.end_time}
            className="bg-yellow-500 text-white px-4 py-2 rounded-lg hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {schedulingEvent ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Scheduling...
              </>
            ) : (
              <>
                <Calendar className="mr-2 h-4 w-4" />
                Schedule Event
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const EditTaskModal = ({ onClose, editFormData, setEditFormData, updatingTask, onUpdate, availableUsers }) => {
  const { handleBackdropMouseDown, handleBackdropMouseUp } = useModal(true, onClose);

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onMouseDown={handleBackdropMouseDown}
      onMouseUp={handleBackdropMouseUp}
    >
      <div
        className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold">Edit Task</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            &times;
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Title *
            </label>
            <input
              type="text"
              value={editFormData.title || ''}
              onChange={(e) => setEditFormData(prev => ({ ...prev, title: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
              placeholder="Enter task title"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              rows={3}
              value={editFormData.description || ''}
              onChange={(e) => setEditFormData(prev => ({ ...prev, description: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
              placeholder="Enter task description"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes
            </label>
            <textarea
              rows={2}
              value={editFormData.notes || ''}
              onChange={(e) => setEditFormData(prev => ({ ...prev, notes: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
              placeholder="Additional notes"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Assigned To
            </label>
            <select
              value={editFormData.assigned_to || ''}
              onChange={(e) => setEditFormData(prev => ({ ...prev, assigned_to: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
            >
              <option value="">Unassigned</option>
              {availableUsers.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.first_name} {user.last_name}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Priority
              </label>
              <select
                value={editFormData.priority || 'medium'}
                onChange={(e) => setEditFormData(prev => ({ ...prev, priority: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Due Date
              </label>
              <input
                type="date"
                value={editFormData.due_date || ''}
                onChange={(e) => setEditFormData(prev => ({ ...prev, due_date: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
              <div className="flex items-center mt-2">
                <input
                  type="checkbox"
                  id="court_deadline"
                  checked={editFormData.court_deadline || false}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, court_deadline: e.target.checked }))}
                  className="h-4 w-4 text-yellow-600 focus:ring-yellow-500 border-gray-300 rounded"
                />
                <label htmlFor="court_deadline" className="ml-2 block text-sm text-gray-900">
                  Court Deadline
                </label>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Estimated Hours
            </label>
            <input
              type="number"
              min="0"
              step="0.25"
              value={editFormData.estimated_hours || ''}
              onChange={(e) => setEditFormData(prev => ({ ...prev, estimated_hours: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
              placeholder="0.0"
            />
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="billable"
              checked={editFormData.billable || false}
              onChange={(e) => setEditFormData(prev => ({ ...prev, billable: e.target.checked }))}
              className="h-4 w-4 text-yellow-600 focus:ring-yellow-500 border-gray-300 rounded"
            />
            <label htmlFor="billable" className="ml-2 block text-sm text-gray-900">
              Billable Time
            </label>
          </div>
        </div>

        <div className="mt-8 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
            disabled={updatingTask}
          >
            Cancel
          </button>
          <button
            onClick={onUpdate}
            disabled={updatingTask || !editFormData.title || (editFormData.court_deadline && !editFormData.due_date)}
            className="bg-yellow-500 text-white px-6 py-2 rounded-lg hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {updatingTask ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Updating...
              </>
            ) : (
              <>
                <Edit className="mr-2 h-4 w-4" />
                Update Task
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};


const ReminderModal = ({ onClose, reminders, onCreateReminder, onDeleteReminder, savingReminder }) => {
  const [reminderDate, setReminderDate] = useState('');
  const { handleBackdropMouseDown, handleBackdropMouseUp } = useModal(true, onClose);

  // Default to tomorrow at 9:00 AM
  useState(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(9, 0, 0, 0);
    const pad = (n) => String(n).padStart(2, '0');
    setReminderDate(
      `${tomorrow.getFullYear()}-${pad(tomorrow.getMonth() + 1)}-${pad(tomorrow.getDate())}T${pad(tomorrow.getHours())}:${pad(tomorrow.getMinutes())}`
    );
  });

  const handleSubmit = () => {
    if (!reminderDate) return;
    onCreateReminder(reminderDate);
    // Reset to next default after save
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(9, 0, 0, 0);
    const pad = (n) => String(n).padStart(2, '0');
    setReminderDate(
      `${tomorrow.getFullYear()}-${pad(tomorrow.getMonth() + 1)}-${pad(tomorrow.getDate())}T${pad(tomorrow.getHours())}:${pad(tomorrow.getMinutes())}`
    );
  };

  const isPast = reminderDate && new Date(reminderDate) <= new Date();

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onMouseDown={handleBackdropMouseDown}
      onMouseUp={handleBackdropMouseUp}
    >
      <div
        className="bg-white rounded-lg p-6 w-full max-w-md mx-4"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold flex items-center">
            <Bell className="mr-2 h-5 w-5" />
            Task Reminders
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            &times;
          </button>
        </div>

        {/* Existing reminders */}
        {reminders.length > 0 && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Active Reminders</label>
            <div className="space-y-2">
              {reminders.filter(r => !r.is_sent).map((reminder) => (
                <div key={reminder.id} className="flex items-center justify-between p-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-center">
                    <Bell className="h-4 w-4 text-yellow-600 mr-2" />
                    <span className="text-sm text-gray-800">
                      {new Date(reminder.remind_at).toLocaleString()}
                    </span>
                  </div>
                  <button
                    onClick={() => onDeleteReminder(reminder.id)}
                    className="text-red-400 hover:text-red-600 p-1"
                    title="Delete reminder"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Past (sent) reminders */}
        {reminders.filter(r => r.is_sent).length > 0 && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-500 mb-2">Sent Reminders</label>
            <div className="space-y-1">
              {reminders.filter(r => r.is_sent).map((reminder) => (
                <div key={reminder.id} className="flex items-center justify-between p-2 bg-gray-50 border border-gray-200 rounded-lg">
                  <span className="text-sm text-gray-500 line-through">
                    {new Date(reminder.remind_at).toLocaleString()}
                  </span>
                  <span className="text-xs text-green-600">Sent</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* New reminder form */}
        <div className="border-t border-gray-200 pt-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Set New Reminder
          </label>
          <input
            type="datetime-local"
            value={reminderDate}
            onChange={(e) => setReminderDate(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
          />
          {isPast && (
            <p className="text-xs text-red-500 mt-1">Reminder time must be in the future</p>
          )}
        </div>

        <div className="mt-6 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            Close
          </button>
          <button
            onClick={handleSubmit}
            disabled={savingReminder || !reminderDate || isPast}
            className="bg-yellow-500 text-white px-4 py-2 rounded-lg hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {savingReminder ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Saving...
              </>
            ) : (
              <>
                <Bell className="mr-2 h-4 w-4" />
                Add Reminder
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const TaskDetail = () => {
  const { id } = useParams();
  const { user: currentUser } = useAuth();
  const { notification, showSuccess, showError, showWarning, hideNotification } = useNotification();
  const [task, setTask] = useState(null);
  const [caseData, setCaseData] = useState(null);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeTracking, setTimeTracking] = useState(false);
  const [timerSeconds, setTimerSeconds] = useState(0);
  const timerIntervalRef = useRef(null);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [statusHistory, setStatusHistory] = useState([]);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [assigningUser, setAssigningUser] = useState(false);
  const [showTimeLogModal, setShowTimeLogModal] = useState(false);
  const [loggingTime, setLoggingTime] = useState(false);
  const [showStatusHistoryModal, setShowStatusHistoryModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [updatingTask, setUpdatingTask] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleData, setScheduleData] = useState({
    title: '',
    date: '',
    start_time: '',
    end_time: '',
    description: ''
  });
  const [schedulingEvent, setSchedulingEvent] = useState(false);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [documentData, setDocumentData] = useState({
    name: '',
    description: '',
    file: null
  });
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [reminders, setReminders] = useState([]);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [savingReminder, setSavingReminder] = useState(false);

  const startLocalTimer = useCallback(() => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
    }

    const interval = setInterval(() => {
      setTimerSeconds(prev => prev + 1);
    }, 1000);

    timerIntervalRef.current = interval;
  }, []);

  const fetchTaskData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch task details
      const taskResponse = await taskService.getTask(id);
      setTask(taskResponse.data);

      // Fetch task status history
      const historyResponse = await taskService.getTaskStatusHistory(id);
      setStatusHistory(historyResponse.data || []);

      // Fetch available users for assignment (exclude inactive firm members and pending invitations)
      try {
        const usersResponse = await userService.getUsers();
        const activeUsers = (usersResponse.data || []).filter(u => u.firm_active !== false && !u.has_pending_invitation);
        setAvailableUsers(activeUsers);
      } catch (userError) {
        console.error('Error fetching users:', userError);
      }

      // Fetch task reminders
      try {
        const remindersResponse = await taskService.getReminders(id);
        setReminders(remindersResponse.data || []);
      } catch (reminderError) {
        console.error('Error fetching reminders:', reminderError);
      }

      // Get timer status and set up local state
      const timerResponse = await taskService.getTimerStatus(id);
      const timerData = timerResponse.data;
      setTimeTracking(timerData.is_running);

      // Initialize timer seconds from actual_hours if available, otherwise use timer_seconds
      const initialSeconds = taskResponse.data.actual_hours
        ? Math.round(taskResponse.data.actual_hours * 3600)
        : timerData.timer_seconds;
      setTimerSeconds(initialSeconds);

      // If timer is running, start the local counter
      if (timerData.is_running) {
        startLocalTimer();
      }

      // If task has case_id and project_id, fetch related data
      const taskData = taskResponse.data;
      if (taskData.case_id) {
        try {
          const caseResponse = await caseService.getCase(taskData.case_id);
          setCaseData(caseResponse.data);
          // Store context for Tools page
          localStorage.setItem('lastViewedCase', JSON.stringify({
            caseId: taskData.case_id,
            caseTitle: caseResponse.data.title,
            caseNumber: caseResponse.data.case_number,
          }));
        } catch (caseError) {
          console.error('Error fetching case data:', caseError);
        }
      }

      if (taskData.project_id) {
        try {
          const projectResponse = await projectService.getProject(taskData.project_id);
          setProject(projectResponse.data);
          // Store context for Tools page
          localStorage.setItem('lastViewedProject', JSON.stringify({
            projectId: taskData.project_id,
            projectName: projectResponse.data.name,
          }));
        } catch (projectError) {
          console.error('Error fetching project data:', projectError);
        }
      }

    } catch (error) {
      console.error('Error fetching task:', error);
      setError('Failed to load task details');
    } finally {
      setLoading(false);
    }
  }, [id, startLocalTimer]);

  useEffect(() => {
    fetchTaskData();
  }, [fetchTaskData]);

  // Cleanup timer interval when component unmounts
  useEffect(() => {
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, []);


  const stopLocalTimer = () => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'todo':
        return 'bg-gray-100 text-gray-800';
      case 'in_progress':
        return 'bg-yellow-100 text-yellow-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'high':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'medium':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'low':
        return 'text-green-600 bg-green-50 border-green-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'urgent':
      case 'high':
        return <AlertTriangle className="h-4 w-4" />;
      default:
        return <CheckCircle className="h-4 w-4" />;
    }
  };

  const isOverdue = task && task.due_date && new Date(task.due_date) < new Date() && task.status !== 'completed';

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
    } else {
      return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    }
  };

  const handleStatusSelect = (newStatus) => {
    if (newStatus !== task.status) {
      setPendingStatus(newStatus);
    } else {
      setPendingStatus(null);
    }
  };

  const handleStatusSave = async () => {
    if (!pendingStatus) return;
    
    try {
      // Call the API to update task status
      await taskService.updateTaskStatus(task.id, {
        status: pendingStatus,
        comment: `Status changed from ${task.status} to ${pendingStatus}`
      });
      
      // Update local state
      setTask(prev => ({ ...prev, status: pendingStatus }));
      setPendingStatus(null);
      
      // Refresh status history
      const historyResponse = await taskService.getTaskStatusHistory(task.id);
      setStatusHistory(historyResponse.data);
      
      showSuccess('Task status updated');

    } catch (error) {
      console.error('Error updating task status:', error);
      showError('Failed to update task status. Please try again.');
    }
  };

  const handleStatusCancel = () => {
    setPendingStatus(null);
  };

  const handleTimeTracking = async () => {
    try {
      if (timeTracking) {
        // Stop the timer
        const response = await taskService.stopTimer(id);
        const timerData = response.data;
        
        setTimeTracking(false);
        setTimerSeconds(timerData.timer_seconds);
        stopLocalTimer();
        
        // Update task's actual_hours to reflect the new time
        setTask(prev => ({
          ...prev,
          actual_hours: timerData.timer_seconds / 3600
        }));
      } else {
        // Start the timer
        const response = await taskService.startTimer(id);
        const timerData = response.data;
        
        setTimeTracking(true);
        setTimerSeconds(timerData.timer_seconds);
        startLocalTimer();
      }
    } catch (error) {
      console.error('Error toggling timer:', error);
      showError('Failed to update the timer. Please try again.');
    }
  };

  const handleAssignTask = async (userId) => {
    try {
      setAssigningUser(true);
      
      // Update task assignment
      const response = await taskService.updateTask(task.id, {
        assigned_to: userId
      });
      
      // Update local task state
      const updatedTask = response.data;
      
      // Find the assigned user to get their name and email
      const assignedUser = availableUsers.find(user => user.id === userId);
      if (assignedUser) {
        updatedTask.assigned_to_name = `${assignedUser.first_name} ${assignedUser.last_name}`;
        updatedTask.assigned_to_email = assignedUser.email;
      }
      
      setTask(updatedTask);
      setShowAssignModal(false);
      
    } catch (error) {
      console.error('Error assigning task:', error);
      showError('Failed to assign task. Please try again.');
    } finally {
      setAssigningUser(false);
    }
  };

  const handleUnassignTask = async () => {
    try {
      setAssigningUser(true);
      
      // Remove task assignment
      const response = await taskService.updateTask(task.id, {
        assigned_to: null
      });
      
      // Update local task state
      const updatedTask = response.data;
      updatedTask.assigned_to_name = null;
      updatedTask.assigned_to_email = null;
      
      setTask(updatedTask);
      setShowAssignModal(false);
      
    } catch (error) {
      console.error('Error unassigning task:', error);
      showError('Failed to remove assignment. Please try again.');
    } finally {
      setAssigningUser(false);
    }
  };

  const handleEditTask = () => {
    // Initialize form data with current task values
    setEditFormData({
      title: task.title || '',
      description: task.description || '',
      notes: task.notes || '',
      priority: task.priority || 'medium',
      due_date: task.due_date ? task.due_date.split('T')[0] : '',
      estimated_hours: task.estimated_hours || '',
      billable: task.billable || false,
      court_deadline: task.court_deadline || false,
      assigned_to: task.assigned_to || ''
    });
    setShowEditModal(true);
  };

  const handleUpdateTask = async () => {
    try {
      setUpdatingTask(true);

      // Prepare update data
      const updateData = {
        title: editFormData.title,
        description: editFormData.description,
        notes: editFormData.notes,
        priority: editFormData.priority,
        due_date: editFormData.due_date ? (() => {
          // Use user's timezone preference, fallback to browser timezone
          const timezone = currentUser?.default_timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
          // Create a date at noon in the user's timezone
          const dateAtNoon = new Date(`${editFormData.due_date}T12:00:00`);
          const formatter = new Intl.DateTimeFormat('en-US', {
            timeZone: timezone,
            timeZoneName: 'shortOffset'
          });
          const parts = formatter.formatToParts(dateAtNoon);
          const offsetPart = parts.find(p => p.type === 'timeZoneName')?.value || '+00:00';
          // Convert offset like "GMT-5" to "-05:00"
          const match = offsetPart.match(/GMT([+-]?)(\d+)?(?::(\d+))?/);
          let tzOffset = '+00:00';
          if (match) {
            const sign = match[1] || '+';
            const hours = String(match[2] || 0).padStart(2, '0');
            const minutes = String(match[3] || 0).padStart(2, '0');
            tzOffset = `${sign}${hours}:${minutes}`;
          }
          return `${editFormData.due_date}T12:00:00${tzOffset}`;
        })() : null,
        estimated_hours: parseFloat(editFormData.estimated_hours) || 0,
        billable: editFormData.billable,
        court_deadline: editFormData.court_deadline,
        assigned_to: editFormData.assigned_to || null
      };

      // Update task
      const response = await taskService.updateTask(task.id, updateData);

      // Update local state with enriched user info
      const updatedTask = response.data;
      if (editFormData.assigned_to) {
        const assignedUser = availableUsers.find(user => user.id === editFormData.assigned_to);
        if (assignedUser) {
          updatedTask.assigned_to_name = `${assignedUser.first_name} ${assignedUser.last_name}`;
          updatedTask.assigned_to_email = assignedUser.email;
        }
      } else {
        updatedTask.assigned_to_name = null;
        updatedTask.assigned_to_email = null;
      }

      setTask(updatedTask);
      setShowEditModal(false);
      setEditFormData({});

    } catch (error) {
      console.error('Error updating task:', error);
      showError('Failed to update task. Please try again.');
    } finally {
      setUpdatingTask(false);
    }
  };

  const handleCreateScheduleEvent = async () => {
    try {
      setSchedulingEvent(true);

      // Validate required fields
      if (!scheduleData.title || !scheduleData.date || !scheduleData.start_time || !scheduleData.end_time) {
        showWarning('Please fill in all required fields');
        return;
      }

      // Create calendar event using the proper API
      const eventData = {
        title: scheduleData.title,
        description: scheduleData.description,
        start_datetime: `${scheduleData.date}T${scheduleData.start_time}:00`,
        end_datetime: `${scheduleData.date}T${scheduleData.end_time}:00`,
        event_type: 'work_session',
        related_task_id: task.id,
        related_case_id: task.case_id,
        all_day: false
      };

      // Call the calendar API to create the event
      await calendarService.createEvent(eventData);

      // Reset form and close modal
      setScheduleData({
        title: '',
        date: '',
        start_time: '',
        end_time: '',
        description: ''
      });
      setShowScheduleModal(false);

      showSuccess('Calendar event scheduled successfully! You can view it in the Calendar page.');

    } catch (error) {
      console.error('Error scheduling event:', error);
      let errorMessage = 'Failed to schedule event. Please try again.';

      if (error.response?.data?.detail) {
        errorMessage = `Failed to schedule event: ${error.response.data.detail}`;
      } else if (error.response?.status === 409) {
        errorMessage = 'There is a scheduling conflict. Please choose a different time.';
      }

      showError(errorMessage);
    } finally {
      setSchedulingEvent(false);
    }
  };

  const handleDocumentUpload = async () => {
    try {
      setUploadingDocument(true);

      // Validate required fields
      if (!documentData.name || !documentData.file) {
        showWarning('Please provide a document name and select a file');
        return;
      }

      // Create FormData for file upload
      const formData = new FormData();
      formData.append('file', documentData.file);
      formData.append('name', documentData.name);
      formData.append('description', documentData.description || '');
      formData.append('task_id', task.id);
      if (task.case_id) {
        formData.append('case_id', task.case_id);
      }
      if (task.project_id) {
        formData.append('project_id', task.project_id);
      }

      // Upload document using the proper API
      await documentService.uploadDocument(formData);

      // Reset form and close modal
      setDocumentData({
        name: '',
        description: '',
        file: null
      });
      setShowDocumentModal(false);

      showSuccess('Document uploaded successfully!');

    } catch (error) {
      console.error('Error uploading document:', error);
      let errorMessage = 'Failed to upload document. Please try again.';

      if (error.response?.data?.detail) {
        errorMessage = `Failed to upload document: ${error.response.data.detail}`;
      } else if (error.response?.status === 413) {
        errorMessage = 'File is too large. Please select a smaller file.';
      } else if (error.response?.status === 415) {
        errorMessage = 'File type not supported. Please select a different file.';
      }

      showError(errorMessage);
    } finally {
      setUploadingDocument(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setDocumentData(prev => ({
        ...prev,
        file,
        name: prev.name || file.name.split('.')[0] // Auto-populate name if empty
      }));
    }
  };

  const handleCreateReminder = async (reminderDate) => {
    try {
      setSavingReminder(true);
      await taskService.createReminder(task.id, { remind_at: new Date(reminderDate).toISOString() });
      // Refresh reminders list
      const response = await taskService.getReminders(task.id);
      setReminders(response.data || []);
      showSuccess('Reminder set successfully');
    } catch (error) {
      console.error('Error creating reminder:', error);
      const msg = error.response?.data?.detail || 'Failed to set reminder';
      showError(msg);
    } finally {
      setSavingReminder(false);
    }
  };

  const handleDeleteReminder = async (reminderId) => {
    try {
      await taskService.deleteReminder(task.id, reminderId);
      setReminders(prev => prev.filter(r => r.id !== reminderId));
      showSuccess('Reminder removed');
    } catch (error) {
      console.error('Error deleting reminder:', error);
      showError('Failed to delete reminder');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  if (error || !task) {
    return (
      <div className="text-center py-12">
        <div className="text-red-500 mb-4">
          <CheckCircle className="h-16 w-16 mx-auto" />
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Task Not Found</h2>
        <p className="text-gray-600 mb-4">{error || 'The requested task could not be found.'}</p>
        <Link
          to="/cases"
          className="bg-yellow-500 text-white px-4 py-2 rounded-lg hover:bg-yellow-600"
        >
          Back to Cases
        </Link>
      </div>
    );
  }

  const breadcrumbItems = [
    ...(caseData ? [{ label: `${caseData.case_number} - ${caseData.title}`, icon: Briefcase, to: `/cases/${caseData.id}` }] : []),
    ...(project ? [{ label: project.name, icon: FolderOpen, to: `/projects/${project.id}` }] : []),
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div>
            <Breadcrumb items={breadcrumbItems} />
            <div className="flex items-center space-x-3">
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <CheckCircle className="mr-3 h-8 w-8" />
                {task.title}
              </h1>
              <span className={`px-3 py-1 text-sm rounded-full ${getStatusColor(task.status)}`}>
                {task.status.replace('_', ' ')}
              </span>
              {task.court_deadline && (
                <span className="px-2 py-1 text-xs bg-red-100 text-red-800 rounded-full">
                  Court Deadline
                </span>
              )}
              {isOverdue && (
                <span className="px-2 py-1 text-xs bg-red-500 text-white rounded-full">
                  Overdue
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <button 
            onClick={handleTimeTracking}
            className={`px-3 py-2 rounded-lg flex items-center ${
              timeTracking 
                ? 'bg-red-600 text-white hover:bg-red-700' 
                : 'bg-green-600 text-white hover:bg-green-700'
            }`}
          >
            {timeTracking ? <Square className="mr-2 h-4 w-4" /> : <Play className="mr-2 h-4 w-4" />}
            {timeTracking ? 'Stop Timer' : 'Start Timer'}
          </button>
          <button 
            onClick={handleEditTask}
            className="bg-yellow-500 text-white px-4 py-2 rounded-lg flex items-center hover:bg-yellow-600"
          >
            <Edit className="mr-2 h-4 w-4" />
            Edit Task
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Task Details */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">Task Details</h2>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Description</label>
                <p className="text-gray-900 mt-1">{task.description}</p>
              </div>
              
              {task.notes && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Notes</label>
                  <p className="text-gray-900 mt-1">{task.notes}</p>
                </div>
              )}
            </div>
          </div>

          {/* Status & Actions */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Status & Actions</h2>
              {pendingStatus && (
                <div className="flex items-center space-x-2">
                  <button
                    onClick={handleStatusSave}
                    className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700"
                  >
                    Save Changes
                  </button>
                  <button
                    onClick={handleStatusCancel}
                    className="bg-gray-500 text-white px-3 py-1 rounded text-sm hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
            
            {pendingStatus && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  <strong>Pending change:</strong> {task.status} → {pendingStatus}
                </p>
                <p className="text-xs text-yellow-600 mt-1">
                  Click "Save Changes" to confirm this status update.
                </p>
              </div>
            )}
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <button
                onClick={() => handleStatusSelect('todo')}
                className={`p-3 rounded-lg border-2 text-center transition-colors ${
                  pendingStatus === 'todo'
                    ? 'border-yellow-500 bg-gray-50 text-yellow-600 ring-2 ring-blue-200' 
                    : task.status === 'todo' 
                      ? 'border-yellow-500 bg-gray-50 text-yellow-600' 
                      : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="font-medium">To Do</div>
                {pendingStatus === 'todo' && (
                  <div className="text-xs mt-1 text-yellow-600">Click Save to confirm</div>
                )}
              </button>
              <button
                onClick={() => handleStatusSelect('in_progress')}
                className={`p-3 rounded-lg border-2 text-center transition-colors ${
                  pendingStatus === 'in_progress'
                    ? 'border-yellow-500 bg-yellow-50 text-yellow-700 ring-2 ring-yellow-200' 
                    : task.status === 'in_progress' 
                      ? 'border-yellow-500 bg-yellow-50 text-yellow-700' 
                      : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="font-medium">In Progress</div>
                {pendingStatus === 'in_progress' && (
                  <div className="text-xs mt-1 text-yellow-600">Click Save to confirm</div>
                )}
              </button>
              <button
                onClick={() => handleStatusSelect('completed')}
                className={`p-3 rounded-lg border-2 text-center transition-colors ${
                  pendingStatus === 'completed'
                    ? 'border-green-500 bg-green-50 text-green-700 ring-2 ring-green-200' 
                    : task.status === 'completed' 
                      ? 'border-green-500 bg-green-50 text-green-700' 
                      : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="font-medium">Completed</div>
                {pendingStatus === 'completed' && (
                  <div className="text-xs mt-1 text-green-600">Click Save to confirm</div>
                )}
              </button>
              <button
                onClick={() => handleStatusSelect('cancelled')}
                className={`p-3 rounded-lg border-2 text-center transition-colors ${
                  pendingStatus === 'cancelled'
                    ? 'border-red-500 bg-red-50 text-red-700 ring-2 ring-red-200' 
                    : task.status === 'cancelled' 
                      ? 'border-red-500 bg-red-50 text-red-700' 
                      : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="font-medium">Cancelled</div>
                {pendingStatus === 'cancelled' && (
                  <div className="text-xs mt-1 text-red-600">Click Save to confirm</div>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Task Info */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Task Information</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Priority</span>
                <span className={`px-2 py-1 text-xs rounded-full border flex items-center ${getPriorityColor(task.priority)}`}>
                  {getPriorityIcon(task.priority)}
                  <span className="ml-1">{task.priority.toUpperCase()}</span>
                </span>
              </div>
              <div className="flex justify-between items-start">
                <span className="text-gray-600">Assigned To</span>
                <div className="text-right">
                  {task.assigned_to_name ? (
                    <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                      <div className="font-medium">{task.assigned_to_name}</div>
                      <div className="text-sm text-gray-500">{task.assigned_to_email}</div>
                      <button 
                        onClick={() => setShowAssignModal(true)}
                        className="inline-flex items-center px-2 py-1 mt-2 text-xs font-medium text-white bg-yellow-500 rounded-md hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-1 transition-colors"
                      >
                        <Users className="w-3 h-3 mr-1" />
                        Reassign
                      </button>
                    </div>
                  ) : (
                    <button 
                      onClick={() => setShowAssignModal(true)}
                      className="font-medium text-yellow-600 hover:text-gray-800"
                    >
                      Assign to Someone
                    </button>
                  )}
                </div>
              </div>
              {task.due_date && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Due Date</span>
                  <span className={`font-medium ${isOverdue ? 'text-red-600' : ''}`}>
                    {new Date(task.due_date).toLocaleDateString()}
                  </span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-600">Billable</span>
                <span className={`font-medium ${task.billable ? 'text-green-600' : 'text-gray-600'}`}>
                  {task.billable ? 'Yes' : 'No'}
                </span>
              </div>
            </div>
          </div>

          {/* Time Tracking */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <Clock className="mr-2 h-5 w-5" />
              Time Tracking
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Estimated Hours</span>
                <span className="font-medium">{task.estimated_hours}h</span>
              </div>
              
              {/* Consolidated Timer/Total Time Display */}
              <div className={`flex justify-between items-center p-3 rounded-lg border ${
                timeTracking 
                  ? 'bg-green-50 border-green-200' 
                  : 'bg-gray-50 border-gray-200'
              }`}>
                <div className="flex items-center">
                  <span className={`font-medium ${
                    timeTracking ? 'text-green-700' : 'text-gray-700'
                  }`}>
                    Total Time
                  </span>
                  {timeTracking && (
                    <span className="ml-2 px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                      Running
                    </span>
                  )}
                </div>
                <span className={`font-mono text-lg font-bold ${
                  timeTracking ? 'text-green-700' : 'text-gray-900'
                }`}>
                  {formatTime(timerSeconds)}
                </span>
              </div>
              <div className="pt-2">
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all duration-300 ${
                      (task.actual_hours / task.estimated_hours) > 1 ? 'bg-red-500' : 'bg-gray-500'
                    }`}
                    style={{ 
                      width: `${Math.min((task.actual_hours / task.estimated_hours) * 100, 100)}%` 
                    }}
                  ></div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Modals */}
      {showAssignModal && (
        <AssignModal
          onClose={() => setShowAssignModal(false)}
          task={task}
          assigningUser={assigningUser}
          onUnassign={handleUnassignTask}
          onAssign={handleAssignTask}
          availableUsers={availableUsers}
        />
      )}
      {showTimeLogModal && (
        <TimeLogModal
          onClose={() => setShowTimeLogModal(false)}
          task={task}
          loggingTime={loggingTime}
          setLoggingTime={setLoggingTime}
          setTask={setTask}
          timerSeconds={timerSeconds}
          setTimerSeconds={setTimerSeconds}
          showWarning={showWarning}
          showError={showError}
        />
      )}
      {showStatusHistoryModal && (
        <StatusHistoryModal
          onClose={() => setShowStatusHistoryModal(false)}
          statusHistory={statusHistory}
        />
      )}
      {showEditModal && (
        <EditTaskModal
          onClose={() => { setShowEditModal(false); setEditFormData({}); }}
          editFormData={editFormData}
          setEditFormData={setEditFormData}
          updatingTask={updatingTask}
          onUpdate={handleUpdateTask}
          availableUsers={availableUsers}
        />
      )}
      {showScheduleModal && (
        <ScheduleTimeModal
          onClose={() => { setShowScheduleModal(false); setScheduleData({ title: '', date: '', start_time: '', end_time: '', description: '' }); }}
          scheduleData={scheduleData}
          setScheduleData={setScheduleData}
          schedulingEvent={schedulingEvent}
          onSchedule={handleCreateScheduleEvent}
        />
      )}
      {showDocumentModal && (
        <AddDocumentModal
          onClose={() => { setShowDocumentModal(false); setDocumentData({ name: '', description: '', file: null }); }}
          documentData={documentData}
          setDocumentData={setDocumentData}
          uploadingDocument={uploadingDocument}
          onUpload={handleDocumentUpload}
          onFileChange={handleFileChange}
        />
      )}
      {showReminderModal && (
        <ReminderModal
          onClose={() => setShowReminderModal(false)}
          reminders={reminders}
          onCreateReminder={handleCreateReminder}
          onDeleteReminder={handleDeleteReminder}
          savingReminder={savingReminder}
        />
      )}

      {/* Notification Toast */}
      <Notification
        isOpen={notification.isOpen}
        onClose={hideNotification}
        title={notification.title}
        message={notification.message}
        variant={notification.variant}
        duration={notification.duration}
      />
    </div>
  );
};

export default TaskDetail;